import React, { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { importCsv, fetchFile, fetchFiles } from "../../../services/aro/importService";
import { fetchEvents } from "../../../services/aro/eventService";
import { ImportResponse, FileRecord, AROEvent } from "../../../types/aro";
import { useAuth } from "../../../context/useAuth";
import { canAccessQuarantine, canViewImportHistory } from "../../../utils/quarantineAccess";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import EmptyState     from "../shared/EmptyState/EmptyState";
import { formatDateLong } from "../../../utils/formatters";
import "./ImportPage.css";

type CertType   = "auto" | "ai_week" | "external";
type ActiveView = "upload" | "history";

interface CsvPreview { headers: string[]; rows: string[][]; }

const REQUIRED_COLS = ["email", "certificate_name", "earned_date", "full_name"];
const ALLOWED_EXTS  = [".csv", ".xlsx", ".xls"];


function parseCsvPreview(text: string): CsvPreview {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return { headers: [], rows: [] };
  const parse = (line: string) => line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
  return { headers: parse(lines[0]), rows: lines.slice(1, 6).map(parse) };
}

function isValidEmail(v: string) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }
function isValidDate(v: string)  { return !!v && !isNaN(Date.parse(v)); }
function fileExt(name: string)   { return name.slice(name.lastIndexOf(".")).toLowerCase(); }

function formatEventDateForApi(isoDate: string): string {
  if (!isoDate) return isoDate;
  const [y, m, d] = isoDate.split("-");
  return `${parseInt(m)}/${parseInt(d)}/${y}`;
}

function StatusPill({ status }: { status: FileRecord["status"] }) {
  return <span className={`aro-status-pill aro-status-pill--${status}`}>{status}</span>;
}

export default function ImportPage() {
  const fileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { email: uploadedBy } = useAuth();
  const canViewHistory = canViewImportHistory(uploadedBy);

  // ── Form state ────────────────────────────────────────────
  const [certType,         setCertType]         = useState<CertType>("auto");
  const [file,             setFile]             = useState<File | null>(null);
  const [defaultEventName,    setDefaultEventName]    = useState("");
  const [defaultEventDate,    setDefaultEventDate]    = useState("");
  const [defaultEventEndDate, setDefaultEventEndDate] = useState("");
  const [preview,          setPreview]          = useState<CsvPreview | null>(null);
  const [formError,        setFormError]        = useState<string | null>(null);

  // ── Event dropdown state ──────────────────────────────────
  const [events,        setEvents]        = useState<AROEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>(""); // event_id or "custom"

  // ── Submission / polling state ────────────────────────────
  const [submitting,   setSubmitting]   = useState(false);
  const [startResult,  setStartResult]  = useState<ImportResponse | null>(null);
  const [polledFile,   setPolledFile]   = useState<FileRecord | null>(null);
  const [polling,      setPolling]      = useState(false);
  const [expandErrors, setExpandErrors] = useState(false);

  // ── History state ─────────────────────────────────────────
  const [activeView,          setActiveView]          = useState<ActiveView>("upload");
  const [historyFiles,        setHistoryFiles]        = useState<FileRecord[]>([]);
  const [historyLoading,      setHistoryLoading]      = useState(false);
  const [historyError,        setHistoryError]        = useState<string | null>(null);
  const [selectedHistoryId,   setSelectedHistoryId]   = useState<string | null>(null);
  const [historyDetail,       setHistoryDetail]       = useState<FileRecord | null>(null);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  const [historyExpandErrors, setHistoryExpandErrors] = useState(false);

  // ── Load history & events ─────────────────────────────────
  const loadHistory = useCallback(() => {
    setHistoryLoading(true);
    setHistoryError(null);
    fetchFiles({ limit: 50 })
      .then((r) => setHistoryFiles(r.files))
      .catch((e) => setHistoryError(e.message))
      .finally(() => setHistoryLoading(false));
  }, []);

  useEffect(() => {
    loadHistory();
    fetchEvents().then((r) => setEvents(r.events)).catch(() => {});
  }, [loadHistory]);

  // ── Polling ───────────────────────────────────────────────
  useEffect(() => {
    if (!polling || !startResult?.file_id) return;
    const interval = setInterval(async () => {
      try {
        const f = await fetchFile(startResult.file_id);
        setPolledFile(f);
        if (f.status === "complete" || f.status === "failed") {
          setPolling(false);
          loadHistory();
        }
      } catch {
        // ignore transient errors, keep polling
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [polling, startResult, loadHistory]);

  // ── File handling ─────────────────────────────────────────
  const applyFile = (f: File) => {
    if (!ALLOWED_EXTS.includes(fileExt(f.name))) {
      setFormError("Only .csv, .xlsx, or .xls files are supported.");
      return;
    }
    setFile(f);
    setFormError(null);
    setStartResult(null);
    setPolledFile(null);
    setExpandErrors(false);
    const ext = fileExt(f.name);
    if (ext === ".csv") {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(parseCsvPreview(ev.target?.result as string));
      reader.readAsText(f);
    } else {
      setPreview(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) applyFile(f); else { setFile(null); setPreview(null); }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) applyFile(f);
  };

  const removeFile = () => {
    setFile(null);
    setPreview(null);
    setFormError(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  // ── Event selection ───────────────────────────────────────
  const handleEventSelect = (eventId: string) => {
    setSelectedEvent(eventId);
    if (eventId === "" || eventId === "custom") {
      setDefaultEventName("");
      setDefaultEventDate("");
      setDefaultEventEndDate("");
      return;
    }
    const ev = events.find((e) => String(e.id) === eventId);
    if (ev) {
      setDefaultEventName(ev.event_name);
      setDefaultEventDate(ev.start_date);
      setDefaultEventEndDate(ev.end_date ?? "");
    }
  };

  const missingCols = preview ? REQUIRED_COLS.filter((c) => !preview.headers.includes(c)) : [];

  // ── Validation ────────────────────────────────────────────
  const validate = (): string | null => {
    if (!file)                                      return "Please select a file.";
    if (!ALLOWED_EXTS.includes(fileExt(file.name))) return "Only .csv, .xlsx, or .xls files are supported.";
    if (!uploadedBy.trim())                         return "Uploaded by (email) is required.";
    if (!isValidEmail(uploadedBy.trim()))           return "Uploaded by must be a valid email address.";
    if (defaultEventDate && !isValidDate(defaultEventDate)) return "Default event date must be a valid date.";
    return null;
  };

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }

    setSubmitting(true);
    setFormError(null);
    setStartResult(null);
    setPolledFile(null);
    setExpandErrors(false);

    const fd = new FormData();
    fd.append("file",        file!);
    fd.append("uploaded_by", uploadedBy.trim());
    if (certType !== "auto")     fd.append("cert_type",          certType);
    if (defaultEventName.trim()) fd.append("default_event_name", defaultEventName.trim());
    if (defaultEventDate) {
      const dateValue = defaultEventDate && defaultEventEndDate
        ? `${formatEventDateForApi(defaultEventDate)} - ${formatEventDateForApi(defaultEventEndDate)}`
        : formatEventDateForApi(defaultEventDate);
      fd.append("default_event_date", dateValue);
    }

    try {
      const res = await importCsv(fd);
      setStartResult(res);
      setPolling(true);
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFile(null); setPreview(null); setStartResult(null);
    setPolledFile(null); setFormError(null); setPolling(false); setExpandErrors(false);
    setSelectedEvent(""); setDefaultEventName(""); setDefaultEventDate(""); setDefaultEventEndDate("");
    if (fileRef.current) fileRef.current.value = "";
  };

  // ── History row click ─────────────────────────────────────
  const handleHistoryRowClick = async (f: FileRecord) => {
    if (selectedHistoryId === f.file_id) {
      setSelectedHistoryId(null);
      setHistoryDetail(null);
      setHistoryExpandErrors(false);
      return;
    }
    setSelectedHistoryId(f.file_id);
    setHistoryDetail(null);
    setHistoryExpandErrors(false);
    setHistoryDetailLoading(true);
    try {
      setHistoryDetail(await fetchFile(f.file_id));
    } catch {
      setHistoryDetail(f);
    } finally {
      setHistoryDetailLoading(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────
  const showEventFields  = certType === "auto" || certType === "ai_week";
  const showAiWeekNudge  = certType === "ai_week" && (!defaultEventName.trim() || !defaultEventDate);
  const isDone           = polledFile?.status === "complete" || polledFile?.status === "failed";
  const isCustomEvent    = selectedEvent === "custom" || selectedEvent === "";

  return (
    <div className="aro-page">
      <div className="aro-import-header">
        <h1 className="aro-import-title">Import Certificates</h1>
        <p className="aro-import-subtitle">Upload a CSV or XLSX file to bulk-add certificate earners.</p>
      </div>

      {/* View tabs */}
      <div className="aro-tabs">
        <button
          className={`aro-tab ${activeView === "upload" ? "aro-tab--active" : ""}`}
          onClick={() => setActiveView("upload")}
        >
          Upload
        </button>
        {canViewHistory && (
          <button
            className={`aro-tab ${activeView === "history" ? "aro-tab--active" : ""}`}
            onClick={() => { setActiveView("history"); loadHistory(); }}
          >
            Upload History
          </button>
        )}
      </div>

      {/* ── Upload view ──────────────────────────────────────── */}
      {activeView === "upload" && (
        <>
          <div className="aro-import-body">

            {/* Drag-drop zone */}
            <div
              className={`aro-dropzone ${file ? "aro-dropzone--has-file" : ""}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => !file && !startResult && fileRef.current?.click()}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              {file ? (
                <span className="aro-dropzone__file-row">
                  <span className="aro-dropzone__filename">📄 {file.name}</span>
                  <button
                    className="aro-dropzone__remove"
                    onClick={(e) => { e.stopPropagation(); removeFile(); }}
                    title="Remove file"
                  >
                    ✕
                  </button>
                </span>
              ) : (
                <>
                  <span className="aro-dropzone__icon">⬆</span>
                  <span className="aro-dropzone__text">Drag &amp; drop a CSV, XLSX, or XLS file here, or click to browse</span>
                </>
              )}
            </div>

            {/* Missing columns warning */}
            {preview && missingCols.length > 0 && (
              <div className="aro-warn-banner">
                ⚠ Missing required columns: <strong>{missingCols.join(", ")}</strong>
              </div>
            )}

            {/* CSV preview */}
            {file && [".xlsx", ".xls"].includes(fileExt(file.name)) && (
              <div className="aro-preview">
                <p className="aro-preview__label">Preview not available for Excel files — your file will be processed on upload.</p>
              </div>
            )}
            {preview && preview.headers.length > 0 && (
              <div className="aro-preview">
                <p className="aro-preview__label">Preview (first 5 rows)</p>
                <div className="aro-preview__scroll">
                  <table className="aro-table aro-preview__table">
                    <thead>
                      <tr>{preview.headers.map((h) => <th key={h}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, i) => (
                        <tr key={i} className={i % 2 === 1 ? "aro-table__row--odd" : ""}>
                          {row.map((cell, j) => <td key={j}>{cell}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Form fields */}
            <div className="aro-import-fields">
              <div className="aro-import-field">
                <label className="aro-import-label">Uploaded By</label>
                <input
                  className="aro-input"
                  value={uploadedBy}
                  readOnly
                  style={{ background: "var(--color-bg-secondary)", cursor: "default" }}
                />
              </div>

              <div className="aro-import-field">
                <label className="aro-import-label aro-import-label--required">Certificate Type</label>
                <select
                  className="aro-select"
                  value={certType}
                  onChange={(e) => setCertType(e.target.value as CertType)}
                >
                  <option value="auto">(auto-detect per row)</option>
                  <option value="ai_week">AI Week</option>
                  <option value="external">External</option>
                </select>
              </div>

              {showEventFields && (
                <>
                  <div className="aro-import-field">
                    <label className="aro-import-label">
                      Default Event Name <span className="aro-muted">(Optional)</span>
                    </label>
                    <select
                      className="aro-select"
                      value={selectedEvent}
                      onChange={(e) => handleEventSelect(e.target.value)}
                    >
                      <option value="">— Select existing event —</option>
                      {events.map((ev) => (
                        <option key={ev.id} value={ev.id}>
                          {ev.event_name} ({formatDateLong(ev.start_date)}{ev.end_date ? ` – ${formatDateLong(ev.end_date)}` : ""})
                        </option>
                      ))}
                      <option value="custom">Enter custom name…</option>
                    </select>
                    {isCustomEvent && (
                      <input
                        className="aro-input"
                        placeholder="Ohio AI Week Spring 2026"
                        value={defaultEventName}
                        onChange={(e) => setDefaultEventName(e.target.value)}
                        style={{ marginTop: "var(--space-2)" }}
                      />
                    )}
                  </div>
                  <div className="aro-import-field">
                    <label className="aro-import-label">
                      Default Event Start Date <span className="aro-muted">(Optional)</span>
                    </label>
                    <input
                      className="aro-input"
                      type="date"
                      value={defaultEventDate}
                      readOnly={!isCustomEvent}
                      style={!isCustomEvent ? { background: "var(--color-bg-secondary)", cursor: "default" } : {}}
                      onChange={(e) => isCustomEvent && setDefaultEventDate(e.target.value)}
                    />
                  </div>
                  <div className="aro-import-field">
                    <label className="aro-import-label">
                      Default Event End Date <span className="aro-muted">(Optional — leave blank for single-day)</span>
                    </label>
                    <input
                      className="aro-input"
                      type="date"
                      value={defaultEventEndDate}
                      readOnly={!isCustomEvent}
                      min={defaultEventDate || undefined}
                      style={!isCustomEvent ? { background: "var(--color-bg-secondary)", cursor: "default" } : {}}
                      onChange={(e) => isCustomEvent && setDefaultEventEndDate(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>

            {/* AI Week nudge */}
            {showAiWeekNudge && (
              <div className="aro-import-help aro-import-help--nudge">
                💡 AI Week imports work best with a default event name and date — fill them in above if the file doesn't include <code>event_name</code> / <code>event_date</code> columns per row.
              </div>
            )}

            {/* Mode help text */}
            {certType !== "auto" && (
              <p className="aro-import-help">
                {certType === "ai_week"
                  ? "All rows will be treated as AI Week certificates."
                  : "All rows will be treated as External certificates. No event fields required."}
              </p>
            )}

            {formError && <div className="aro-error-banner">{formError}</div>}

            {/* Polling / result state */}
            {startResult && !isDone && (
              <div className="aro-import-polling">
                <span className="aro-import-polling__spinner" />
                <span>
                  Import started — tracking <strong>{startResult.filename}</strong>
                  {startResult.total_rows ? ` (${startResult.total_rows} rows)` : ""}…
                </span>
              </div>
            )}

            {isDone && polledFile && (
              <div className={`aro-import-result aro-import-result--${polledFile.status}`}>
                {polledFile.status === "complete" ? (
                  <>
                    <p className="aro-import-result__title">✓ Import complete</p>
                    <div className="aro-import-result__stats">
                      <span>Rows processed: <strong>{polledFile.total_rows ?? startResult?.total_rows ?? "—"}</strong></span>
                      <span>Inserted: <strong>{polledFile.inserted ?? 0}</strong></span>
                      <span>Skipped (duplicates): <strong>{polledFile.skipped ?? 0}</strong></span>
                      <span>Errors: <strong>{polledFile.error_count ?? 0}</strong></span>
                    </div>
                    {(polledFile.error_details?.length ?? 0) > 0 && (
                      <div className="aro-import-result__errors">
                        <button
                          className="aro-import-result__expand-btn"
                          onClick={() => setExpandErrors((v) => !v)}
                        >
                          {expandErrors ? "▲ Hide" : "▼ Show"} {polledFile.error_details.length} error/skip message{polledFile.error_details.length !== 1 ? "s" : ""}
                        </button>
                        {expandErrors && (
                          <ul className="aro-import-result__error-list">
                            {polledFile.error_details.map((msg, i) => <li key={i}>{msg}</li>)}
                          </ul>
                        )}
                      </div>
                    )}
                    <button className="aro-btn aro-btn--ghost aro-import-result__reset" onClick={resetForm}>
                      Import another file
                    </button>
                  </>
                ) : (
                  <>
                    <p className="aro-import-result__title">✕ Import failed</p>
                    <p className="aro-import-result__msg">
                      {polledFile.error_details?.[0] ?? "An unknown error occurred."}
                    </p>
                    <button className="aro-btn aro-btn--ghost aro-import-result__reset" onClick={resetForm}>
                      Try again
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Submit button — hidden once import is started */}
            {!startResult && (
              <div className="aro-import-actions">
                <button
                  className="aro-btn aro-btn--primary"
                  onClick={handleSubmit}
                  disabled={submitting || !file}
                >
                  {submitting ? "Uploading…" : "Submit Import"}
                </button>
              </div>
            )}
          </div>

          {/* Column reference */}
          <div className="aro-ref-card">
            <h3 className="aro-ref-card__title">Column Reference</h3>
            <div className="aro-ref-card__cols">
              <div>
                <span className="aro-ref-card__label">Required</span>
                <div className="aro-ref-card__tags">
                  {REQUIRED_COLS.map((c) => (
                    <span key={c} className="aro-ref-tag aro-ref-tag--required">{c}</span>
                  ))}
                </div>
              </div>
              <div>
                <span className="aro-ref-card__label">Optional</span>
                <div className="aro-ref-card__tags">
                  {["linkedin_url", "company", "event_name", "event_date"].map((c) => (
                    <span key={c} className="aro-ref-tag">{c}</span>
                  ))}
                </div>
              </div>
            </div>
            <p className="aro-ref-card__tip">
              💡 LinkedIn URLs are optional but required for employment tracking. Ask attendees to provide them at registration.
            </p>
          </div>

        </>
      )}

      {/* ── History view ─────────────────────────────────────── */}
      {activeView === "history" && canViewHistory && (
        <div className="aro-card aro-card--no-pad">
          {historyLoading ? (
            <LoadingSpinner />
          ) : historyError ? (
            <div className="aro-error-banner" style={{ margin: "var(--space-4)" }}>{historyError}</div>
          ) : historyFiles.length === 0 ? (
            <EmptyState icon="📂" message="No uploads yet." />
          ) : (
            <>
              <table className="aro-table">
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Uploaded By</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Inserted</th>
                    <th>Skipped</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {historyFiles.map((f, i) => (
                    <tr
                      key={f.file_id}
                      className={[
                        i % 2 === 1 ? "aro-table__row--odd" : "",
                        "aro-table__row--clickable",
                        selectedHistoryId === f.file_id ? "aro-history-row--selected" : "",
                      ].join(" ")}
                      onClick={() => handleHistoryRowClick(f)}
                    >
                      <td className="aro-table__cell--primary">{f.file_name}</td>
                      <td>{f.uploaded_by}</td>
                      <td>{formatDateLong(f.uploaded_at)}</td>
                      <td className="aro-muted">{f.cert_type ?? "auto"}</td>
                      <td><StatusPill status={f.status} /></td>
                      <td>{f.inserted ?? "—"}</td>
                      <td>{f.skipped ?? "—"}</td>
                      <td>{f.error_count ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Detail panel */}
              {selectedHistoryId && (
                <div className="aro-history-detail">
                  {historyDetailLoading ? (
                    <p className="aro-muted">Loading details…</p>
                  ) : historyDetail ? (
                    <>
                      <p className="aro-history-detail__title">{historyDetail.file_name}</p>
                      <div className="aro-import-result__stats">
                        <span>Total rows: <strong>{historyDetail.total_rows ?? "—"}</strong></span>
                        <span>Inserted: <strong>{historyDetail.inserted ?? "—"}</strong></span>
                        <span>Skipped: <strong>{historyDetail.skipped ?? "—"}</strong></span>
                        <span>Errors: <strong>{historyDetail.error_count ?? "—"}</strong></span>
                      </div>
                      {canAccessQuarantine(uploadedBy) && (
                        <button
                          className="aro-link-btn"
                          onClick={() => navigate(`/ai-ready-ohio/quarantine?file_id=${historyDetail.file_id}`)}
                        >
                          View Quarantined Rows →
                        </button>
                      )}
                      {(historyDetail.error_details?.length ?? 0) > 0 && (
                        <div className="aro-import-result__errors">
                          <button
                            className="aro-import-result__expand-btn"
                            onClick={() => setHistoryExpandErrors((v) => !v)}
                          >
                            {historyExpandErrors ? "▲ Hide" : "▼ Show"} {historyDetail.error_details.length} message{historyDetail.error_details.length !== 1 ? "s" : ""}
                          </button>
                          {historyExpandErrors && (
                            <ul className="aro-import-result__error-list">
                              {historyDetail.error_details.map((msg, i) => <li key={i}>{msg}</li>)}
                            </ul>
                          )}
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
